import {
	BrowserClient,
	defaultStackParser,
	getDefaultIntegrations,
	makeFetchTransport,
	Scope,
} from '@sentry/browser';

import { BFFError, BFFNetworkError } from '../../common/errors';
import { getEnvironment } from '../environment';

import { DEFAULT_SENTRY_CONFIG, SENTRY_DSN } from './constants';
import type { SentryClientConfig, SentryEventTags } from './types';

let sentryClient: BrowserClient | undefined;
let scope: Scope = new Scope();

export function logException(exception: Error | unknown, tags: SentryEventTags) {
	scope.setTags(tags);

	if (exception instanceof BFFError) {
		const error = exception as BFFError;

		scope.setExtras({
			name: error.name,
			context: error.context,
			status: error.status,
			timestamp: error.timestamp,
			originalError: error.originalError,
			...(error instanceof BFFNetworkError && { url: error.url }),
		});
	}

	sentryClient?.captureException(exception, undefined, scope);

	// Unset tags to prevent an instance of the error boundary from logging with a specific tag,
	// and then another instance from wrongly passing it despite not having the tag.
	for (const [key, _] of Object.entries(tags)) {
		scope.setTag(key, undefined);
	}

	// Unset any extras to prevent them from persisting
	scope.setExtras({});
}

export function setupSentryClient({ sentryConfig }: { sentryConfig?: SentryClientConfig }) {
	const { dsn = SENTRY_DSN, env } = sentryConfig || {};

	if (!sentryClient) {
		const environment = getEnvironment(env);

		// filter integrations that use the global variable
		const integrations = getDefaultIntegrations({}).filter((defaultIntegration) => {
			return !['BrowserApiErrors', 'Breadcrumbs', 'GlobalHandlers'].includes(
				defaultIntegration.name,
			);
		});

		sentryClient = new BrowserClient({
			...DEFAULT_SENTRY_CONFIG,
			dsn,
			environment,
			beforeSend: (event) => {
				// We only want errors captured explicitly via logException to avoid unwanted errors being sent
				if (event.tags?.teamName === undefined) {
					return null;
				}
				return event;
			},
			attachStacktrace: true,
			transport: makeFetchTransport,
			stackParser: defaultStackParser,
			integrations,
		});

		scope.setClient(sentryClient);
		sentryClient.init();
	}
}
